import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  API_BASE_URL,
  deleteEvents,
  getEvents,
  patchEvents,
  postEvents,
} from "../../services/api";
import { useNotification } from "../../contexts/NotificationContext";

const MainContainer = styled.div`
  max-width: 1100px;
  margin: 10px auto;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;
  padding: 10px;

  @media (max-width: 1024px) {
    max-width: 95%;
    padding: 15px;
  }

  @media (max-width: 768px) {
    padding: 12px;
    margin: 5px auto;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 8px;
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }
`;

const FormContainer = styled.form`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 25px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 20px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
`;

const TabButton = styled.button`
  padding: 8px 16px;
  border: none;
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#666'};
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  border-radius: 4px 4px 0 0;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : '#f5f5f5'};
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    flex: 1;
    padding: 8px 10px;
    font-size: 12px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 12px;

  @media (max-width: 768px) {
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }

  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #34495e;
    font-size: 12px;

    @media (max-width: 768px) {
      font-size: 14px;
      margin-bottom: 8px;
      font-weight: 600;
    }

    @media (max-width: 480px) {
      font-size: 13px;
      margin-bottom: 6px;
    }
  }

  input,
  textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 12px;
    transition: all 0.3s;
    box-sizing: border-box;

    @media (max-width: 768px) {
      padding: 12px;
      font-size: 16px;
      border-radius: 6px;
    }

    @media (max-width: 480px) {
      padding: 10px;
      font-size: 16px;
      border-radius: 5px;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
    }
  }

  textarea {
    min-height: 80px;
    resize: vertical;

    @media (max-width: 768px) {
      min-height: 100px;
    }

    @media (max-width: 480px) {
      min-height: 90px;
    }
  }
`;

const DateTimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 11px;
  margin-top: 4px;
  display: block;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;

  @media (max-width: 768px) {
    gap: 10px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 15px;
  }
`;

const Button = styled.button`
  background-color: ${(props) =>
    props.red
      ? "#e74c3c"
      : props.secondary
        ? "#7f8c8d"
        : "var(--primary-color)"};
  color: white;
  padding: ${(props) => (props.small ? "6px 10px" : "8px 14px")};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: ${(props) => (props.small ? "10px" : "11px")};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex: ${(props) => (props.small ? "1" : "none")};
  min-width: 0;
  touch-action: manipulation;

  @media (max-width: 768px) {
    padding: ${(props) => (props.small ? "10px 12px" : "12px 20px")};
    font-size: ${(props) => (props.small ? "13px" : "14px")};
    border-radius: 6px;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    padding: ${(props) => (props.small ? "8px 10px" : "10px 16px")};
    font-size: ${(props) => (props.small ? "12px" : "13px")};
    min-height: 40px;
    flex: 1;
  }

  &:hover {
    background-color: ${(props) =>
    props.red
      ? "#c0392b"
      : props.secondary
        ? "#6c7a7a"
        : "var(--primary-color)"};
    transform: translateY(-1px);
    filter: brightness(0.9);

    @media (max-width: 480px) {
      transform: none;
    }
  }

  &:active {
    transform: translateY(0);
    filter: brightness(0.8);
  }

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }
`;

const EventsSection = styled.div`
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 25px;
  }

  @media (max-width: 480px) {
    margin-top: 20px;
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eaeaea;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 18px;
    padding-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 15px;
  }
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const EventCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 15px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  border-left: 2px solid var(--primary-color);
  max-width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);

    @media (max-width: 480px) {
      transform: none;
    }
  }

  @media (max-width: 768px) {
    padding: 18px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 15px;
  }
`;

const EventHeader = styled.div`
  margin-bottom: 10px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const EventTitle = styled.h4`
  margin: 0 0 6px 0;
  color: #2c3e50;
  font-size: 13px;
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const EventDate = styled.p`
  margin: 0;
  color: #7f8c8d;
  font-size: 11px;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const PlaceInfo = styled.p`
  margin: 0 0 10px 0;
  color: #3498db;
  font-size: 11px;
  padding: 4px 8px;
  background: #ecf6fd;
  border-radius: 4px;
  display: inline-block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 6px 10px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 5px 8px;
  }
`;

const Description = styled.p`
  margin: 0 0 12px 0;
  color: #555;
  line-height: 1.5;
  font-size: 11px;
  flex-grow: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: 54px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 15px;
    -webkit-line-clamp: 4;
    max-height: 90px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 12px;
    -webkit-line-clamp: 3;
    max-height: 60px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    gap: 10px;
    padding-top: 12px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 25px;
  }

  @media (max-width: 480px) {
    gap: 5px;
    margin-top: 20px;
  }
`;

const PageButton = styled.button`
  padding: 6px 10px;
  border: 1px solid ${(props) => (props.active ? "var(--primary-color)" : "#ddd")};
  background-color: ${(props) => (props.active ? "var(--primary-color)" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#333")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border-radius: 4px;
  font-size: 11px;
  transition: all 0.2s;
  min-width: 32px;
  opacity: ${(props) => (props.disabled ? "0.5" : "1")};
  touch-action: manipulation;

  &:hover:not(:disabled) {
    background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "#f5f5f5"};
    border-color: var(--primary-color);

    @media (max-width: 480px) {
      background-color: ${(props) =>
    props.active ? "var(--primary-color)" : "#fff"};
    }
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 14px;
    min-width: 40px;
    min-height: 40px;
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
    font-size: 13px;
    min-width: 36px;
    min-height: 36px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px;
  color: #95a5a6;
  background: #f9f9f9;
  border-radius: 6px;
  font-size: 12px;

  @media (max-width: 768px) {
    padding: 40px 20px;
    font-size: 15px;
    border-radius: 8px;
  }

  @media (max-width: 480px) {
    padding: 30px 15px;
    font-size: 14px;
  }
`;

const MobileScrollHint = styled.div`
  text-align: center;
  padding: 8px;
  background: #e8f4fd;
  border-radius: 4px;
  font-size: 11px;
  color: #2980b9;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 10px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 8px;
  }
`;

const LanguageSwitcher = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  justify-content: center;
`;

const LangButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.active ? 'var(--primary-color)' : '#ddd'};
  background: ${props => props.active ? 'var(--primary-color)' : '#fff'};
  color: ${props => props.active ? '#fff' : '#666'};
  cursor: pointer;
  border-radius: 4px;
  font-size: 11px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }
`;

function Events() {
  const [eventses, setEventses] = useState([]);
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    translations: {
      kz: { eventName: "", description: "", place: "" },
      ru: { eventName: "", description: "", place: "" },
      en: { eventName: "", description: "", place: "" }
    }
  });
  const [activeTab, setActiveTab] = useState("kz");
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewLang, setViewLang] = useState("kz");
  const eventsPerPage = 9;
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { showNotification } = useNotification();

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "startDate" || name === "endDate") {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [activeTab]: {
            ...prev.translations[activeTab],
            [name]: value
          }
        }
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file || null);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  };

  const handleEdit = (event) => {
    const translations = {
      kz: { eventName: "", description: "", place: "" },
      ru: { eventName: "", description: "", place: "" },
      en: { eventName: "", description: "", place: "" }
    };

    if (event.eventName?.translations) {
      event.eventName.translations.forEach(t => {
        if (translations[t.languageCode]) {
          translations[t.languageCode].eventName = t.textValue;
        }
      });
    }
    if (event.eventDescription?.translations) {
      event.eventDescription.translations.forEach(t => {
        if (translations[t.languageCode]) {
          translations[t.languageCode].description = t.textValue;
        }
      });
    }
    if (event.eventPlace?.translations) {
      event.eventPlace.translations.forEach(t => {
        if (translations[t.languageCode]) {
          translations[t.languageCode].place = t.textValue;
        }
      });
    }

    setFormData({
      startDate: event.startDate || "",
      endDate: event.endDate || "",
      translations
    });
    setImageFile(null);
    setImagePreview(event.img_url ? API_BASE_URL + event.img_url : null);
    setEditId(event.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log(formData)

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      if (imageFile) {
        fd.append("img_url", imageFile);
      }
      fd.append("startDate", formData.startDate || "");
      fd.append("endDate", formData.endDate || "");
      fd.append("translations", JSON.stringify(formData.translations));

      if (editId) {
        await patchEvents(editId, fd);

        const response = await getEvents();
        if (response?.events) {
          const sortedEvents = response.events.sort(
            (a, b) => {
              const dateA = a.startDate || a.endDate;
              const dateB = b.startDate || b.endDate;
              if (!dateA && !dateB) return 0;
              if (!dateA) return 1;
              if (!dateB) return -1;
              return new Date(dateB) - new Date(dateA);
            }
          );
          setEventses(sortedEvents);
        }
        showNotification("Событие успешно обновлено", "success");
      } else {
        const response = await postEvents(fd);
        if (response?.event) {
          setEventses((prev) => [response.event, ...prev]);
        }
        showNotification("Событие успешно создано", "success");
      }

      setFormData({
        startDate: "",
        endDate: "",
        translations: {
          kz: { eventName: "", description: "", place: "" },
          ru: { eventName: "", description: "", place: "" },
          en: { eventName: "", description: "", place: "" }
        }
      });
      setEditId(null);
      setImageFile(null);
      setImagePreview(null);
      setErrors({});
    } catch (error) {
      showNotification("Ошибка при сохранении", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Удалить это событие?")) {
      try {
        await deleteEvents(id);
        setEventses((prev) => {
          const updatedEvents = prev.filter((e) => e.id !== id);
          const maxPage = Math.ceil(updatedEvents.length / eventsPerPage);
          if (currentPage > maxPage && currentPage > 1) {
            setCurrentPage(maxPage);
          }
          return updatedEvents;
        });
        showNotification("Событие успешно удалено", "success");
      } catch (error) {
        showNotification("Ошибка при удалении", "error");
        console.log(error);
      }
    }
  };

  const cancelEdit = () => {
    setFormData({
      startDate: "",
      endDate: "",
      translations: {
        kz: { eventName: "", description: "", place: "" },
        ru: { eventName: "", description: "", place: "" },
        en: { eventName: "", description: "", place: "" }
      }
    });
    setEditId(null);
    setImageFile(null);
    setImagePreview(null);
    setErrors({});
  };

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await getEvents();
        console.log("Initial events load:", response);
        if (response?.events) {
          const sortedEvents = response.events.sort(
            (a, b) => {
              const dateA = a.startDate || a.endDate;
              const dateB = b.startDate || b.endDate;
              if (!dateA && !dateB) return 0;
              if (!dateA) return 1;
              if (!dateB) return -1;
              return new Date(dateB) - new Date(dateA);
            }
          );
          setEventses(sortedEvents);
        }
      } catch (error) {
        console.error("Error in loadEvents:", error);
      }
    };
    loadEvents();
  }, []);

  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = eventses.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalPages = Math.ceil(eventses.length / eventsPerPage);

  const getPageNumbers = () => {
    const maxVisiblePages = window.innerWidth <= 480 ? 5 : 10;
    const pageNumbers = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (window.innerWidth <= 768) {
      const eventsSection = document.querySelector('[data-events-section]');
      if (eventsSection) {
        eventsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const validateForm = () => {
    const validationErrors = {};

    if (!formData.startDate && !formData.endDate) {
      validationErrors.startDate = "Укажите хотя бы одну дату";
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      validationErrors.endDate = "Дата окончания не может быть раньше даты начала";
    }

    const langs = ['kz', 'ru', 'en'];
    const hasAtLeastOneLang = langs.some(lang =>
      formData.translations[lang].eventName.trim() &&
      formData.translations[lang].description.trim() &&
      formData.translations[lang].place.trim()
    );

    if (!hasAtLeastOneLang) {
      validationErrors.general = "Заполните хотя бы один язык полностью";
    }

    return validationErrors;
  };

  const getTranslatedText = (textObj, field) => {
    if (!textObj?.translations) return "";

    const langTranslation = textObj.translations.find(t => t.languageCode === viewLang);
    if (langTranslation?.textValue) return langTranslation.textValue;

    const kzTranslation = textObj.translations.find(t => t.languageCode === 'kz');
    if (kzTranslation?.textValue) return kzTranslation.textValue;

    const ruTranslation = textObj.translations.find(t => t.languageCode === 'ru');
    if (ruTranslation?.textValue) return ruTranslation.textValue;

    const enTranslation = textObj.translations.find(t => t.languageCode === 'en');
    if (enTranslation?.textValue) return enTranslation.textValue;

    return "";
  };

  const formatDateStr = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}.${m}.${y}`;
  };

  const formatEventPeriod = (event) => {
    if (event.date) {
      const parts = event.date.split("–").map(s => s.trim());
      if (parts.length === 2 && parts[0] === parts[1]) return parts[0];
      return event.date;
    }
    if (event.startDate && event.endDate) {
      if (event.startDate === event.endDate) {
        return formatDateStr(event.startDate);
      }
      return `${formatDateStr(event.startDate)} – ${formatDateStr(event.endDate)}`;
    }
    if (event.startDate) return formatDateStr(event.startDate);
    if (event.endDate) return formatDateStr(event.endDate);
    return "";
  };

  return (
    <MainContainer>
      <Title>Управление мероприятиями</Title>

      <FormContainer onSubmit={handleSubmit}>
        <DateTimeRow>
          <FormGroup>
            <label>
              Дата начала {editId && "(оставьте пустым, если не хотите менять)"}
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              disabled={loading}
              onChange={handleChange}
            />
            {errors.startDate && <ErrorMessage>{errors.startDate}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <label>
              Дата окончания {editId && "(оставьте пустым, если не хотите менять)"}
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              disabled={loading}
              onChange={handleChange}
            />
            {errors.endDate && <ErrorMessage>{errors.endDate}</ErrorMessage>}
          </FormGroup>
        </DateTimeRow>

        <FormGroup>
          <label>Изображение мероприятия</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={loading}
          />
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              style={{
                marginTop: "8px",
                maxWidth: "200px",
                maxHeight: "120px",
                borderRadius: "4px",
                objectFit: "cover",
              }}
            />
          )}
        </FormGroup>

        <TabContainer>
          <TabButton
            type="button"
            active={activeTab === "kz"}
            onClick={() => setActiveTab("kz")}
          >
            🇰🇿 Қазақша
          </TabButton>
          <TabButton
            type="button"
            active={activeTab === "ru"}
            onClick={() => setActiveTab("ru")}
          >
            🇷🇺 Русский
          </TabButton>
          <TabButton
            type="button"
            active={activeTab === "en"}
            onClick={() => setActiveTab("en")}
          >
            🇬🇧 English
          </TabButton>
        </TabContainer>

        <FormGroup>
          <label>Название мероприятия ({activeTab.toUpperCase()})</label>
          <input
            type="text"
            name="eventName"
            value={formData.translations[activeTab].eventName}
            disabled={loading}
            onChange={handleChange}
            placeholder={`Введите название на ${activeTab === 'kz' ? 'казахском' : activeTab === 'ru' ? 'русском' : 'английском'}`}
          />
        </FormGroup>

        <FormGroup>
          <label>Место проведения ({activeTab.toUpperCase()})</label>
          <input
            type="text"
            name="place"
            value={formData.translations[activeTab].place}
            disabled={loading}
            onChange={handleChange}
            placeholder={`Укажите место на ${activeTab === 'kz' ? 'казахском' : activeTab === 'ru' ? 'русском' : 'английском'}`}
          />
        </FormGroup>

        <FormGroup>
          <label>Описание мероприятия ({activeTab.toUpperCase()})</label>
          <textarea
            name="description"
            value={formData.translations[activeTab].description}
            disabled={loading}
            onChange={handleChange}
            placeholder={`Добавьте описание на ${activeTab === 'kz' ? 'казахском' : activeTab === 'ru' ? 'русском' : 'английском'}`}
          />
        </FormGroup>

        {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

        <ButtonGroup>
          <Button type="submit" disabled={loading}>
            {loading
              ? (editId ? "Обновление..." : "Создание...")
              : (editId ? "Обновить мероприятие" : "Создать мероприятие")
            }
          </Button>
          {editId && (
            <Button type="button" secondary onClick={cancelEdit} disabled={loading}>
              Отменить редактирование
            </Button>
          )}
        </ButtonGroup>
      </FormContainer>

      <EventsSection data-events-section>
        <SectionTitle>Список мероприятий</SectionTitle>

        <LanguageSwitcher>
          <LangButton active={viewLang === "kz"} onClick={() => setViewLang("kz")}>KZ</LangButton>
          <LangButton active={viewLang === "ru"} onClick={() => setViewLang("ru")}>RU</LangButton>
          <LangButton active={viewLang === "en"} onClick={() => setViewLang("en")}>EN</LangButton>
        </LanguageSwitcher>

        {window.innerWidth <= 480 && eventses.length > eventsPerPage && (
          <MobileScrollHint>
            Листайте страницы для просмотра всех мероприятий
          </MobileScrollHint>
        )}

        {eventses.length > 0 ? (
          <>
            <EventsGrid>
              {currentEvents.map((event) => (
                <EventCard key={event.id}>
                  <EventHeader>
                    <EventTitle>{getTranslatedText(event.eventName)}</EventTitle>
                    <EventDate>
                      {formatEventPeriod(event)}
                    </EventDate>
                  </EventHeader>

                  {event.img_url && (
                    <img
                      src={API_BASE_URL + event.img_url}
                      alt=""
                      style={{
                        width: "100%",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        marginBottom: "8px",
                      }}
                    />
                  )}

                  {getTranslatedText(event.eventPlace) && (
                    <PlaceInfo>{getTranslatedText(event.eventPlace)}</PlaceInfo>
                  )}

                  <Description>{getTranslatedText(event.eventDescription)}</Description>

                  <ActionButtons>
                    <Button
                      type="button"
                      small
                      onClick={() => handleEdit(event)}
                      disabled={loading}
                    >
                      Редактировать
                    </Button>
                    <Button
                      type="button"
                      small
                      red
                      onClick={() => handleDelete(event.id)}
                      disabled={loading}
                    >
                      Удалить
                    </Button>
                  </ActionButtons>
                </EventCard>
              ))}
            </EventsGrid>

            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  title="Предыдущая страница"
                >
                  &laquo;
                </PageButton>

                {pageNumbers.map((number) => (
                  <PageButton
                    key={number}
                    active={currentPage === number}
                    onClick={() => paginate(number)}
                    title={`Страница ${number}`}
                  >
                    {number}
                  </PageButton>
                ))}

                <PageButton
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                  title="Следующая страница"
                >
                  &raquo;
                </PageButton>
              </PaginationContainer>
            )}
          </>
        ) : (
          <EmptyState>
            Событий пока нет. Создайте новое мероприятие, используя форму выше.
          </EmptyState>
        )}
      </EventsSection>
    </MainContainer>
  );
}

export default Events;